// 거르는 규칙에 쓰이는 "값" 을 모은다. 앱마다 다른 값(번들 도메인·CLIENT 코드·재발급 경로)은
// ReportingPolicy 로 주입받고, 어디서나 같은 값(브라우저 고정 문구·확장 마커)은 상수로 둔다.
// 규칙의 "로직" 은 classify · shouldDrop 에 있다.

export type ReportingPolicy = {
  // 이 origin 밖의 파일만 있는 스택은 외부 프로그램(확장·주입 스크립트)으로 본다
  bundleOrigins: string[]
  // 400 응답 중 FE 가 잘못된 값을 보낸 것으로 보고할 code
  clientFaultCodes: string[]
  // 항상 버릴 code. 세션 만료처럼 예상된 흐름
  expectedCodes: string[]
  // 5xx 라도 보고할 토큰 재발급 경로 템플릿
  tokenRefreshRoutes: string[]
  // 결제 전용 인스턴스(flow 를 싣는 곳)를 거치지 않는 결제 요청의 경로 템플릿. 결제 흐름으로 취급한다
  paymentRoutes: string[]
  // 실패해도 다음 주기 요청이 못 보낸 몫을 이어 보내는 경로 템플릿. 이 경로의 network 실패는 버린다
  resendingRoutes: string[]
  // toRoute 자동 유도가 틀리는 경로의 예외 (원시 경로 또는 템플릿 → 원하는 템플릿)
  routeOverrides: Record<string, string>
  isOffline: () => boolean
  // 온라인이라고 답해도 요청이 닿지 못하는 순간(화면 숨김·잠자기 복귀 직후 와이파이 재연결 중)인지
  isNetworkUnsettled: () => boolean
}

export type ReportingPolicyInput = Pick<ReportingPolicy, 'bundleOrigins' | 'isOffline'> &
  Partial<Omit<ReportingPolicy, 'bundleOrigins' | 'isOffline'>>

export const definePolicy = (input: ReportingPolicyInput): ReportingPolicy => ({
  clientFaultCodes: [],
  expectedCodes: [],
  tokenRefreshRoutes: [],
  paymentRoutes: [],
  resendingRoutes: [],
  routeOverrides: {},
  isNetworkUnsettled: () => false,
  ...input,
})

export const EXTENSION_STACK_MARKERS: readonly RegExp[] = [
  /chrome-extension:\/\//,
  /moz-extension:\/\//,
  /safari-web-extension:\/\//,
  /UtilityScript/,
]

export const ENVIRONMENT_MESSAGES: readonly RegExp[] = [
  /ResizeObserver loop/,
  /Ignored attempt to cancel a touch\w+ event/,
  /Permissions check failed/,
  // iOS 네이티브가 웹뷰에 주입하는 스크립트의 파싱 실패 문구
  /Unexpected token ';'\. Expected '\)' to end an argument list\./,
  // 앱 웹뷰가 다운로드 fetch 를 가로채 네이티브로 넘길 때
  /Intercepted by RN/,
]

// crossorigin 없이 로드된 다른 출처 스크립트가 던지면 브라우저가 내용을 가리고 이 문구만 준다.
// 메시지지만 브라우저 고정 문구이고, "다른 출처에서 났다" 는 사실 자체가 출처 정보다.
export const CROSS_ORIGIN_MASKED_MESSAGE = /^Script error\.?$/

export const CANCEL_ERROR_NAMES: readonly string[] = [
  'AbortError',
  'CancelledError',
  'CanceledError',
]

export const CANCELED_CODE = 'CANCELED'
